import { isEmpty, isNil, isUndefined } from 'lodash'

import ApiHandler from '@/api/base'
import { OKR_ELEMENT_ENTITY_KEYS } from '@/utils/entity-keys'
import { isJiraIssueOrWorkItem, isKR, isObjective } from '@/utils/objective-types'
import { HOMEPAGE_OKR_SEARCH_TYPE_IDS } from '@/utils/objectives'
import { GRADE_MODES, METRIC_VALUES_KEYS } from '@/utils/okr-element-values'
import { uid } from '@/utils/uid'

// request parameters for getObjectives and exportExcel(and other exports in future should be
// handled in the same way)
const handleObjectivesParameters = payload => {
  const result = { ...payload }
  // delete result.gradeTypes;
  if (result.searchString === '') {
    result.searchString = null
  }

  result.workspaceIds = isNil(result.workspaceIds) ? [result.workspaceId] : result.workspaceIds

  return result
}

export const SEARCH_TYPES = {
  OKR_EXPLORER: 1,
  ALIGNMENT: 2,
  HOMEPAGE: 3,
  HOMEPAGE_OBJECTIVES: HOMEPAGE_OKR_SEARCH_TYPE_IDS.OBJECTIVES,
  HOMEPAGE_KEY_RESULTS: HOMEPAGE_OKR_SEARCH_TYPE_IDS.KEY_RESULTS,
  OKR_EXPLORER_PLAIN: 6,
  METRICS_WITHOUT_PARENTS: 7,
  PROGRESS_VIEW: 8,
  METRICS_WITHOUT_KPI: 9,
  HOMEPAGE_METRICS_PROGRESS_VIEW: 10
}

const enrichOkrElement = okrElement => {
  return {
    ...okrElement,
    uniqueId: okrElement.id,
    uid: uid(),
    childElements: enrichOkrElementList(okrElement.childElements)
  }
}

const enrichOkrElementList = okrElements => {
  return okrElements.map(enrichOkrElement)
}

class ObjectivesApiHandler extends ApiHandler {
  async getObjectives(payload, cancelToken = undefined) {
    const { data } = await this.http.post('/getElements', handleObjectivesParameters(payload), {
      cancelToken
    })
    const isNewResponseFormat = !Array.isArray(data)

    if (isNewResponseFormat) {
      const { elements, ...rest } = data
      return {
        elements: enrichOkrElementList(elements),
        ...rest
      }
    }

    return {
      elements: enrichOkrElementList(data)
    }
  }

  async getChildObjectives(payload) {
    const { data } = await this.http.post('/getNestedElements', {
      ...payload,
      searchString: payload.searchString ? payload.searchString : null // '' -> null
    })

    return {
      ...data,
      items: enrichOkrElementList(data.items)
    }
  }

  async getObjectiveById({ elementId, workspaceId } = {}) {
    const { data } = await this.http.get(`/elements/${elementId}`, {
      params: {
        workspaceId
      }
    })
    return enrichOkrElement(data)
  }

  async getLinkedPlatformElements(payload) {
    const { data } = await this.http.post('/getLinkedPlatformElements', payload)
    return data
  }

  async getParentObjectiveInfo({
    elementTypeId,
    elementId,
    parentId,
    searchString,
    workspaceId = null,
    intervalId,
    strict = false
  } = {}) {
    const { data } = await this.http.post('/getParentObjectiveInfo', {
      elementTypeId,
      elementId,
      parentId,
      intervalId,
      searchString,
      workspaceId,
      strict
    })
    return data
  }

  async createOkrElement(payload) {
    const requestData = {
      // needed only for KR, set default value for other types
      originalValue: 0,
      currentValue: 0,
      targetValue: 0,
      fieldTypeId: 0,
      elementTargetPeriods: undefined,
      periodAggregationTypeId: undefined,
      progressAggregationTypeId: undefined,
      granulationId: undefined,
      thresholdConditionTypeId: undefined,
      ...payload,
      [OKR_ELEMENT_ENTITY_KEYS.WORKSPACE_ID]: Number(payload[OKR_ELEMENT_ENTITY_KEYS.WORKSPACE_ID])
    }
    const { data } = await this.http.post('/elements', requestData)
    return {
      ...data,
      elements: data.elements.map(enrichOkrElement)
    }
  }

  async updateOkrElement({
    contribute,
    description,
    dueDate,
    elementStartDate,
    groups,
    jiraIssueId,
    intervalId,
    name,
    elementId,
    orderValue,
    users,
    parentId,
    levelId,
    workspaceId,
    labelIds,
    confidenceLevelId,
    startDateManual,
    dueDateManual,
    // needed only for KR, set default value for other types
    originalValue = undefined,
    currentValue = undefined,
    targetValue = undefined,
    fieldTypeId = undefined,
    gradeMode = undefined,
    isPrivate = undefined,
    typeId,
    updateStatusForNestedElements = undefined,
    unit = undefined,
    backdate = undefined,
    elementTargetPeriods = undefined,
    periodAggregationTypeId = undefined,
    progressAggregationTypeId = undefined,
    granulationId = undefined,
    thresholdConditionTypeId = undefined,
    viewConfig = null
  } = {}) {
    const payload = {
      contribute,
      description,
      dueDate,
      elementStartDate,
      groups,
      intervalId,
      name,
      elementId,
      orderValue,
      users,
      parentId,
      levelId,
      workspaceId,
      labelIds,
      confidenceLevelId,
      originalValue,
      currentValue,
      targetValue,
      fieldTypeId,
      jiraIssueId,
      startDateManual,
      dueDateManual,
      gradeMode,
      private: isPrivate,
      updateStatusForNestedElements,
      unit,
      backdate,
      elementTargetPeriods,
      periodAggregationTypeId,
      progressAggregationTypeId,
      granulationId,
      thresholdConditionTypeId,
      viewConfig
    }

    if (isJiraIssueOrWorkItem({ typeId }) && gradeMode === GRADE_MODES.AUTO) {
      delete payload[METRIC_VALUES_KEYS.CURRENT_VALUE]
      delete payload[METRIC_VALUES_KEYS.ORIGINAL_VALUE]
      delete payload[METRIC_VALUES_KEYS.TARGET_VALUE]
    }

    if (isKR({ typeId }) || isObjective({ typeId })) {
      delete payload[METRIC_VALUES_KEYS.CURRENT_VALUE]
    }

    if (parentId === elementId) {
      throw new Error('Objective cannot be parent of itself')
    }

    const { data } = await this.http.put('/elements', payload)

    return {
      ...data,
      element: enrichOkrElement(data.element)
    }
  }

  async getHistoryForObject({ objectId, objectTypeId } = {}) {
    const { data } = await this.http.post('/getHistoryForObject', {
      objectId,
      objectTypeId
    })
    return data
  }

  async getProgressHistory({ elementId } = {}) {
    const { data } = await this.http.get(`/elements/${elementId}/getProgressHistory`)
    return data
  }

  async deleteProgressHistoryElement({ elementId, id } = {}) {
    const { data } = await this.http.delete(`/elements/${elementId}/values/${id}`)
    return data
  }

  async copyObjective(payload) {
    const { data } = await this.http.post('/copyObjective', payload)
    return {
      ...data,
      element: enrichOkrElement(data.element)
    }
  }

  async removeOkrElement({ elementId, removeNestedObjectives = false }) {
    const { data } = await this.http.delete('/elements', {
      data: {
        elementId,
        removeNestedObjectives
      }
    })
    return data
  }

  async getExcelExport({
    expandAll = true,
    itemsToOpen = null,
    searchType = SEARCH_TYPES.ALIGNMENT,
    maxOpenLevel = 0,
    offset = 0,
    limit = 1000,
    ...restParams
  } = {}) {
    const response = await this.http.post(
      '/getExcelReport',
      handleObjectivesParameters({
        expandAll,
        itemsToOpen,
        searchType,
        maxOpenLevel,
        limit,
        offset,
        ...restParams
      }),
      { responseType: 'blob', timeout: 0 }
    )
    return {
      data: response.data,
      filename: 'OKR Report.xlsx',
      filetype: response.headers['content-type']
    }
  }

  async getBreadcrumbs({ objectiveId, workspaceId, includeCurrentObjective = true }) {
    const { data } = await this.http.post('/getBreadcrumbs', {
      elementId: objectiveId,
      workspaceId,
      includeCurrentElement: includeCurrentObjective
    })
    return data
  }

  async getCsvExport({ workspaceId }) {
    const response = await this.http.post(
      '/exportElements',
      {
        workspaceId
      },
      { responseType: 'blob' }
    )
    return {
      data: response.data,
      filename: 'elements.csv',
      filetype: response.headers['content-type']
    }
  }

  async moveOkrElement({
    elementId,
    intervalId,
    moveCrossWorkspace,
    moveNested,
    parentId,
    workspaceId,
    conflicts = {}
  }) {
    const { data } = await this.http.post('/move', {
      elementId,
      intervalId,
      moveCrossWorkspace,
      moveNested,
      parentId,
      workspaceId,
      conflicts
    })
    return data
  }

  async checkNestedElementsPermission({ elementId, confidenceLevelId }) {
    const { data } = await this.http.get('/elements/status/permission', {
      params: {
        elementId,
        confidenceLevelId
      }
    })
    return data
  }

  async updateOkrElementCustomFieldValue({ elementId, fieldId, value }) {
    const { data } = await this.http.put(`/elements/${elementId}/customfields`, [
      {
        fieldId,
        value
      }
    ])

    const result = data.customFields[fieldId]

    // BE remove key from response if value is ARRAY Like and empty
    // so to keep FE works as before, we need to return empty array
    if (Array.isArray(value) && isEmpty(value) && isUndefined(result)) {
      return []
    }

    return result
  }

  async checkMovingAvailability({ elementId }) {
    const { data } = await this.http.get(`/move/check?elementId=${elementId}`)

    return data
  }

  async calculateProgressForElement({ elementId, value, date } = {}) {
    const { data } = await this.http.post('/elements/calculateProgress', {
      elementId,
      date,
      value
    })
    return data
  }
}

export default ObjectivesApiHandler
